(() => {
  if (window.__kervelanProductCardModule) return;
  window.__kervelanProductCardModule = true;

  const CARD_SELECTOR = '[data-kv-product-card]';
  const SWATCH_SELECTOR = '[data-kervelan-card-swatch]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const switchTokens = new WeakMap();

  const revealedCards = new WeakSet();
  const observedCards = new WeakSet();

  const revealObserver = reduceMotion.matches
    ? null
    : new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => {
              const rowDelta = a.boundingClientRect.top - b.boundingClientRect.top;
              if (Math.abs(rowDelta) > 8) return rowDelta;
              return a.boundingClientRect.left - b.boundingClientRect.left;
            });

          visible.forEach((entry, index) => {
            const card = entry.target;
            if (!(card instanceof HTMLElement) || revealedCards.has(card)) return;

            card.style.setProperty('--kv-card-reveal-delay', `${index * 110}ms`);
            card.setAttribute('data-kv-card-revealed', 'true');
            revealedCards.add(card);
            revealObserver?.unobserve(card);
          });
        },
        {
          rootMargin: '0px 0px -5% 0px',
          threshold: 0.08,
        }
      );

  const observeCard = (card) => {
    if (!(card instanceof HTMLElement) || observedCards.has(card)) return;

    observedCards.add(card);

    const isCatalogLcpCandidate =
      card.dataset.kvLcpCandidate === 'true' &&
      ['collection', 'search'].includes(card.dataset.cardContext);

    if (isCatalogLcpCandidate || reduceMotion.matches || !revealObserver) {
      card.style.setProperty('--kv-card-reveal-delay', '0ms');
      card.setAttribute('data-kv-card-revealed', 'true');
      revealedCards.add(card);
      return;
    }

    card.setAttribute('data-kv-card-reveal', 'true');
    revealObserver.observe(card);
  };

  const observeCards = (root = document) => {
    if (root instanceof Element && root.matches(CARD_SELECTOR)) observeCard(root);
    root.querySelectorAll?.(CARD_SELECTOR).forEach(observeCard);
  };

  const revealMutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) observeCards(node);
      });
    });
  });

  const mainContent = document.getElementById('MainContent');

  observeCards(mainContent || document);

  if (mainContent) {
    revealMutationObserver.observe(mainContent, { childList: true, subtree: true });
  }

  const setActive = (card, selected) => {
    card.querySelectorAll(SWATCH_SELECTOR).forEach((swatch) => {
      const active = swatch === selected;
      const wasActive =
        swatch.classList.contains('is-active') &&
        swatch.getAttribute('aria-pressed') === 'true';

      if (active === wasActive) return;

      swatch.classList.toggle('is-active', active);
      swatch.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const updateImageElement = (image, source) => {
    if (!(image instanceof HTMLImageElement) || !source?.src) return;
    if (source.srcset) image.srcset = source.srcset;
    else image.removeAttribute('srcset');
    image.src = source.src;
    if (source.alt) image.alt = source.alt;
  };

  const sourceFromSwatch = (swatch, hover = false) => {
    const prefix = hover ? 'hoverImage' : 'image';
    return {
      src: swatch.dataset[`${prefix}Src`] || '',
      srcset: swatch.dataset[`${prefix}Srcset`] || '',
      alt: swatch.dataset[`${prefix}Alt`] || '',
    };
  };

  const updateCard = async (card, swatch) => {
    const primary = card.querySelector('.kv-product-card__image--primary');
    const secondary = card.querySelector('.kv-product-card__image--secondary');
    const price = card.querySelector('[data-kv-card-price]');
    const base = sourceFromSwatch(swatch, false);
    const hover = sourceFromSwatch(swatch, true);
    const token = (switchTokens.get(card) || 0) + 1;
    switchTokens.set(card, token);

    setActive(card, swatch);
    card.classList.add('is-variant-selected');

    const variantUrl = swatch.dataset.variantUrl;
    if (variantUrl) {
      card.querySelectorAll('[data-kv-card-link]').forEach((link) => {
        link.setAttribute('href', variantUrl);
      });
    }

    if (price && swatch.dataset.variantPrice) {
      let currentPrice = price.querySelector('[data-kv-card-current-price]');
      let comparePrice = price.querySelector('[data-kv-card-compare-price]');

      if (!(currentPrice instanceof HTMLElement)) {
        price.textContent = '';
        currentPrice = document.createElement('span');
        currentPrice.className = 'kv-product-card__current-price';
        currentPrice.dataset.kvCardCurrentPrice = '';
        price.append(currentPrice);
      }

      currentPrice.textContent = swatch.dataset.variantPrice;

      const compareValue = swatch.dataset.variantComparePrice || '';
      if (compareValue) {
        if (!(comparePrice instanceof HTMLElement)) {
          comparePrice = document.createElement('span');
          comparePrice.className = 'kv-product-card__compare-price';
          comparePrice.dataset.kvCardComparePrice = '';
          price.insertBefore(comparePrice, currentPrice);
        }
        comparePrice.textContent = compareValue;
      } else if (comparePrice instanceof HTMLElement) {
        comparePrice.remove();
      }
    }

    if (!base.src || !(primary instanceof HTMLImageElement)) return;

    if (!reduceMotion.matches) {
      card.classList.add('is-switching');
      await new Promise((resolve) => window.setTimeout(resolve, 150));
      if (switchTokens.get(card) !== token) return;
    }

    updateImageElement(primary, base);

    if (secondary instanceof HTMLImageElement) {
      if (hover.src) {
        secondary.hidden = false;
        updateImageElement(secondary, hover);
        card.classList.add('has-secondary');
      } else {
        secondary.hidden = true;
        card.classList.remove('has-secondary');
      }
    }

    if (typeof primary.decode === 'function') {
      await Promise.race([
        primary.decode().catch(() => {}),
        new Promise((resolve) => window.setTimeout(resolve, 220)),
      ]);
    }

    if (switchTokens.get(card) !== token) return;
    card.classList.remove('is-switching');
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const swatch = event.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;
    const card = swatch.closest(CARD_SELECTOR);
    if (!card) return;

    event.preventDefault();
    event.stopPropagation();
    void updateCard(card, swatch);
  }, true);

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (!(root instanceof Element)) return;

    if (root.matches(CARD_SELECTOR)) root.classList.remove('is-switching');
    root.querySelectorAll(CARD_SELECTOR).forEach((card) => card.classList.remove('is-switching'));
    observeCards(root);
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    observeCards(document.getElementById('MainContent') || document);
  });
})();
